import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess, type Paginated } from '../../lib/api';

interface EmailLog {
  id: string; to: string; subject: string; status: string;
  messageId: string | null; error: string | null; createdAt: string;
  template: string | null;
}
interface SmsLog {
  id: string; to: string; message: string; status: string;
  provider: string | null; messageId: string | null;
  error: string | null; createdAt: string; template: string | null;
}

interface StatusSummary {
  configured: boolean; connected: boolean;
  sentToday: number; failedToday: number;
  lastSentAt: string | null; lastFailedAt: string | null; lastFailedError: string | null;
}

export function AdminMessagesLogPage() {
  const [tab, setTab] = useState<'failures' | 'email' | 'sms'>('failures');

  const emailStatus = useQuery({
    queryKey: ['admin', 'email-status'],
    queryFn: () => api.get<ApiSuccess<StatusSummary>>('/api/admin/email/status'),
    retry: false,
  });
  const smsStatus = useQuery({
    queryKey: ['admin', 'sms-status'],
    queryFn: () => api.get<ApiSuccess<StatusSummary>>('/api/admin/sms/status'),
    retry: false,
  });

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-2">📬 سجل الرسائل والتنبيهات</h1>
      <p className="text-sm text-gray-500 mb-6">
        تتبّع كل رسالة أُرسلت من المنصة (إيميل أو SMS) ومعرفة وصلت أم فشلت — لمتابعة العملاء الذين لم تصلهم الرسائل.
      </p>

      {/* 🚦 ملخّص سريع */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <SummaryCard
          icon="📧"
          title="الإيميلات"
          status={emailStatus.data?.data}
          isLoading={emailStatus.isLoading}
          color="navy"
        />
        <SummaryCard
          icon="📱"
          title="الرسائل النصية SMS"
          status={smsStatus.data?.data}
          isLoading={smsStatus.isLoading}
          color="gold"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <Tab active={tab === 'failures'} onClick={() => setTab('failures')}>
          🚨 الرسائل الفاشلة
          {((emailStatus.data?.data?.failedToday ?? 0) + (smsStatus.data?.data?.failedToday ?? 0)) > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 mr-1">
              {(emailStatus.data?.data?.failedToday ?? 0) + (smsStatus.data?.data?.failedToday ?? 0)}
            </span>
          )}
        </Tab>
        <Tab active={tab === 'email'} onClick={() => setTab('email')}>📧 سجل الإيميل</Tab>
        <Tab active={tab === 'sms'}   onClick={() => setTab('sms')}>📱 سجل SMS</Tab>
      </div>

      {tab === 'failures' && <FailuresDashboard />}
      {tab === 'email'    && <EmailLogList />}
      {tab === 'sms'      && <SmsLogList />}
    </div>
  );
}

function SummaryCard({ icon, title, status, isLoading, color }: { icon: string; title: string; status: StatusSummary | undefined; isLoading: boolean; color: 'navy' | 'gold' }) {
  if (isLoading) return <Card><CardBody><div className="text-gray-500">⟳ جاري التحميل…</div></CardBody></Card>;
  if (!status) return null;
  const failed = status.failedToday;
  return (
    <Card className={failed > 0 ? 'border-2 border-red-300' : ''}>
      <CardBody>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{icon}</span>
            <h3 className="font-black text-navy-dark">{title}</h3>
          </div>
          {!status.configured ? (
            <Badge color="amber">غير مكوّن</Badge>
          ) : status.connected ? (
            <Badge color="green">● متصل</Badge>
          ) : (
            <Badge color="red">● غير متصل</Badge>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <Stat label="📤 آخر 24س" value={status.sentToday} color="navy" />
          <Stat label="❌ فشل" value={failed} color={failed > 0 ? 'red' : 'gray'} />
          <Stat label="✓ نجح" value={status.sentToday} color="green" />
        </div>
        {status.lastFailedError && (
          <div className="mt-3 text-xs bg-red-50 border border-red-200 rounded p-2 text-red-700">
            ⚠️ آخر خطأ: <span className="font-mono">{status.lastFailedError}</span>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  const colors: Record<string, string> = { navy: 'text-navy-dark', red: 'text-red-600', green: 'text-green-600', gray: 'text-gray-500' };
  return (
    <div className="bg-gray-50 rounded-lg p-2 text-center">
      <div className="text-[10px] text-gray-500 mb-0.5">{label}</div>
      <div className={`font-black text-lg ${colors[color]}`}>{value}</div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ داش الفشل ━━━━━━━━━━━━━━━━━━━━

function FailuresDashboard() {
  const [days, setDays] = useState('7');
  const [type, setType] = useState<'all' | 'email' | 'sms'>('all');

  const emails = useQuery({
    queryKey: ['failed-emails', days],
    queryFn: () => api.get<Paginated<EmailLog>>('/api/admin/email/logs', { query: { status: 'FAILED', limit: 200 } }),
    enabled: type === 'all' || type === 'email',
  });
  const smses = useQuery({
    queryKey: ['failed-sms', days],
    queryFn: () => api.get<Paginated<SmsLog>>('/api/admin/sms/logs', { query: { status: 'FAILED', limit: 200 } }),
    enabled: type === 'all' || type === 'sms',
  });

  const cutoff = new Date(Date.now() - Number(days) * 86_400_000);
  const emailFailures = (emails.data?.data ?? []).filter((e) => new Date(e.createdAt) >= cutoff);
  const smsFailures = (smses.data?.data ?? []).filter((s) => new Date(s.createdAt) >= cutoff);

  // 🎯 لمّ المستلمين اللي فشلت لهم رسائل
  const affected = new Map<string, { email?: number; sms?: number; lastError?: string; lastAt?: string }>();
  for (const e of emailFailures) {
    const k = e.to;
    const cur = affected.get(k) ?? {};
    affected.set(k, { ...cur, email: (cur.email ?? 0) + 1, lastError: e.error ?? cur.lastError, lastAt: e.createdAt > (cur.lastAt ?? '') ? e.createdAt : cur.lastAt });
  }
  for (const s of smsFailures) {
    const k = s.to;
    const cur = affected.get(k) ?? {};
    affected.set(k, { ...cur, sms: (cur.sms ?? 0) + 1, lastError: s.error ?? cur.lastError, lastAt: s.createdAt > (cur.lastAt ?? '') ? s.createdAt : cur.lastAt });
  }
  const sortedAffected = [...affected.entries()].sort((a, b) => (b[1].lastAt ?? '').localeCompare(a[1].lastAt ?? ''));

  return (
    <>
      {/* فلاتر */}
      <Card className="mb-4">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">الفترة</label>
              <select value={days} onChange={(e) => setDays(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm">
                <option value="1">آخر 24 ساعة</option>
                <option value="7">آخر 7 أيام</option>
                <option value="30">آخر 30 يوم</option>
                <option value="90">آخر 90 يوم</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">النوع</label>
              <select value={type} onChange={(e) => setType(e.target.value as never)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm">
                <option value="all">الكل</option>
                <option value="email">إيميل فقط</option>
                <option value="sms">SMS فقط</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 w-full text-center">
                <div className="text-xs text-red-600 mb-0.5">إجمالي الفشل</div>
                <div className="text-2xl font-black text-red-700">{emailFailures.length + smsFailures.length}</div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* العملاء المتأثرون */}
      <h3 className="font-black text-navy-dark mb-3">👥 العملاء الذين لم تصلهم الرسائل</h3>
      {sortedAffected.length === 0 ? (
        <Card><CardBody>
          <div className="text-center py-12 text-gray-500">
            <div className="text-5xl mb-3">✓</div>
            <div className="font-bold text-green-700">ما فيه رسائل فاشلة في هذه الفترة</div>
          </div>
        </CardBody></Card>
      ) : (
        <Card><CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-right px-3 py-2">المستلم</th>
              <th className="text-right px-3 py-2">عدد المحاولات</th>
              <th className="text-right px-3 py-2">آخر خطأ</th>
              <th className="text-right px-3 py-2">آخر محاولة</th>
            </tr></thead>
            <tbody>
              {sortedAffected.slice(0, 100).map(([recipient, info]) => (
                <tr key={recipient} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs" dir="ltr">{recipient}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {info.email && <Badge color="navy">📧 {info.email}</Badge>}
                      {info.sms && <Badge color="gold">📱 {info.sms}</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-red-600 max-w-md truncate" title={info.lastError ?? ''}>{info.lastError ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{info.lastAt ? new Date(info.lastAt).toLocaleString('ar-SA') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody></Card>
      )}
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ سجل الإيميل ━━━━━━━━━━━━━━━━━━━━

function EmailLogList() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: '', q: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['email-logs', filters],
    queryFn: () => api.get<Paginated<EmailLog>>('/api/admin/email/logs', { query: { ...filters, limit: 100 } }),
  });

  const cleanM = useMutation({
    mutationFn: () => api.delete('/api/admin/email/logs'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-logs'] }),
  });

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm">
              <option value="">كل الحالات</option>
              <option value="SENT">✓ مُرسل</option>
              <option value="FAILED">✕ فشل</option>
              <option value="QUEUED">⏳ في الانتظار</option>
              <option value="BOUNCED">↩️ مرتد</option>
            </select>
            <Input placeholder="بحث (إيميل أو موضوع)..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            <Button variant="ghost" onClick={() => { if (confirm('حذف السجلات الأقدم من 90 يوم؟')) cleanM.mutate(); }} loading={cleanM.isPending}>🗑 تنظيف القديم</Button>
          </div>
        </CardBody>
      </Card>

      {isLoading ? (
        <div className="text-gray-500 text-center py-8">⟳ جاري التحميل…</div>
      ) : !data?.data?.length ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500">لا توجد سجلات</div></CardBody></Card>
      ) : (
        <Card><CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-right px-3 py-2">المستلم</th>
              <th className="text-right px-3 py-2">الموضوع</th>
              <th className="text-right px-3 py-2">القالب</th>
              <th className="text-right px-3 py-2">الحالة</th>
              <th className="text-right px-3 py-2">التاريخ</th>
            </tr></thead>
            <tbody>
              {data.data.map((l) => (
                <tr key={l.id} className="border-b">
                  <td className="px-3 py-2 text-xs" dir="ltr">{l.to}</td>
                  <td className="px-3 py-2">
                    <div className="font-bold">{l.subject}</div>
                    {l.error && <div className="text-xs text-red-600">⚠️ {l.error}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{l.template ?? '—'}</td>
                  <td className="px-3 py-2"><Badge color={l.status === 'SENT' ? 'green' : l.status === 'FAILED' ? 'red' : 'amber'}>{l.status}</Badge></td>
                  <td className="px-3 py-2 text-xs text-gray-500">{new Date(l.createdAt).toLocaleString('ar-SA')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody></Card>
      )}
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ سجل SMS ━━━━━━━━━━━━━━━━━━━━

function SmsLogList() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: '', q: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['sms-logs', filters],
    queryFn: () => api.get<Paginated<SmsLog>>('/api/admin/sms/logs', { query: { ...filters, limit: 100 } }),
  });

  const cleanM = useMutation({
    mutationFn: () => api.delete('/api/admin/sms/logs'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-logs'] }),
  });

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm">
              <option value="">كل الحالات</option>
              <option value="SENT">✓ مُرسل</option>
              <option value="DELIVERED">✓✓ مستلم</option>
              <option value="FAILED">✕ فشل</option>
              <option value="REJECTED">⚠️ مرفوض</option>
              <option value="QUEUED">⏳ في الانتظار</option>
            </select>
            <Input placeholder="بحث (جوال أو نص)..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            <Button variant="ghost" onClick={() => { if (confirm('حذف السجلات الأقدم من 90 يوم؟')) cleanM.mutate(); }} loading={cleanM.isPending}>🗑 تنظيف القديم</Button>
          </div>
        </CardBody>
      </Card>

      {isLoading ? (
        <div className="text-gray-500 text-center py-8">⟳ جاري التحميل…</div>
      ) : !data?.data?.length ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500">لا توجد سجلات</div></CardBody></Card>
      ) : (
        <Card><CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-right px-3 py-2">المستلم</th>
              <th className="text-right px-3 py-2">الرسالة</th>
              <th className="text-right px-3 py-2">المزود</th>
              <th className="text-right px-3 py-2">القالب</th>
              <th className="text-right px-3 py-2">الحالة</th>
              <th className="text-right px-3 py-2">التاريخ</th>
            </tr></thead>
            <tbody>
              {data.data.map((l) => (
                <tr key={l.id} className="border-b">
                  <td className="px-3 py-2 text-xs font-mono" dir="ltr">{l.to}</td>
                  <td className="px-3 py-2">
                    <div className="text-xs max-w-md truncate" title={l.message}>{l.message}</div>
                    {l.error && <div className="text-xs text-red-600">⚠️ {l.error}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{l.provider ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{l.template ?? '—'}</td>
                  <td className="px-3 py-2"><Badge color={l.status === 'SENT' || l.status === 'DELIVERED' ? 'green' : l.status === 'FAILED' || l.status === 'REJECTED' ? 'red' : 'amber'}>{l.status}</Badge></td>
                  <td className="px-3 py-2 text-xs text-gray-500">{new Date(l.createdAt).toLocaleString('ar-SA')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody></Card>
      )}
    </>
  );
}

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px whitespace-nowrap inline-flex items-center gap-1 ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}
